import React from 'react';
import {
    Security as Shield,
    TrendingUp,
    Group as Users,
    EmojiEvents as Award,
    CheckCircle,
    WorkspacePremium as Premium
} from '@mui/icons-material';

const AboutPage = () => {
    const teamMembers = [
        {
            name: "Sarah Chen",
            role: "Chief Executive Officer",
            bio: "Former VP of Risk Management at Goldman Sachs with 15+ years in financial risk assessment.",
            avatar: "S"
        },
        {
            name: "Michael Rodriguez",
            role: "Chief Technology Officer",
            bio: "AI/ML expert from Google Brain, specializing in predictive analytics and machine learning systems.",
            avatar: "M"
        },
        {
            name: "Dr. Emily Watson",
            role: "Head of Risk Science",
            bio: "PhD in Risk Analytics from MIT, published researcher in computational risk modeling.",
            avatar: "E"
        },
        {
            name: "David Kim",
            role: "VP of Engineering",
            bio: "Former lead architect at Microsoft Azure, expert in scalable enterprise software solutions.",
            avatar: "D"
        }
    ];

    const values = [
        {
            icon: <Shield style={{ fontSize: 48, color: '#2563eb' }} />,
            title: "Security First",
            description: "We prioritize the highest standards of data security and privacy in everything we build."
        },
        {
            icon: <TrendingUp style={{ fontSize: 48, color: '#16a34a' }} />,
            title: "Innovation",
            description: "Continuously pushing the boundaries of AI and machine learning in risk assessment."
        },
        {
            icon: <Users style={{ fontSize: 48, color: '#7c3aed' }} />,
            title: "Customer Success",
            description: "Your success is our success. We're committed to helping you achieve your risk management goals."
        },
        {
            icon: <Premium style={{ fontSize: 48, color: '#dc2626' }} />,
            title: "Excellence",
            description: "Delivering world-class solutions with attention to detail and commitment to quality."
        }
    ];

    const milestones = [
        { year: "2020", event: "RiskAS founded by risk management veterans" },
        { year: "2021", event: "First AI-powered risk assessment platform launched" },
        { year: "2022", event: "100+ enterprise clients onboarded" },
        { year: "2023", event: "Series A funding raised, expanded to global markets" },
        { year: "2024", event: "750+ clients, $50M+ in prevented losses" }
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            {/* Header Spacer */}
            <div style={{ height: '64px' }}></div>

            {/* Hero Section */}
            <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eff6ff 100%)' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <h1 style={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        marginBottom: '24px',
                        color: '#111827'
                    }}>
                        About RiskAS
                    </h1>
                    <p style={{
                        fontSize: '24px',
                        color: '#6b7280',
                        maxWidth: '800px',
                        margin: '0 auto',
                        lineHeight: '1.6'
                    }}>
                        We're revolutionizing risk management with AI-powered insights,
                        helping businesses make smarter decisions and protect their future.
                    </p>
                </div>
            </section>

            {/* Mission Section */}
            <section style={{ padding: '80px 0', background: 'white' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
                        <div>
                            <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
                                Our Mission
                            </h2>
                            <p style={{ fontSize: '18px', color: '#6b7280', lineHeight: '1.7', marginBottom: '24px' }}>
                                At RiskAS, we believe that every business deserves access to enterprise-grade risk assessment tools.
                                Our mission is to democratize advanced risk analytics through AI-powered solutions that are both
                                powerful and accessible.
                            </p>
                            <p style={{ fontSize: '18px', color: '#6b7280', lineHeight: '1.7' }}>
                                We're committed to helping organizations of all sizes identify, assess, and mitigate risks before
                                they become costly problems, enabling sustainable growth and long-term success.
                            </p>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
                                borderRadius: '16px',
                                padding: '40px',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    fontSize: '48px',
                                    fontWeight: 'bold',
                                    color: '#2563eb',
                                    marginBottom: '16px'
                                }}>
                                    750+
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '18px', marginBottom: '24px' }}>
                                    Trusted Enterprise Clients
                                </div>
                                <div style={{
                                    fontSize: '36px',
                                    fontWeight: 'bold',
                                    color: '#16a34a',
                                    marginBottom: '8px'
                                }}>
                                    $50M+
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '16px' }}>
                                    In Prevented Losses
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section style={{ padding: '80px 0', background: '#f9fafb' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                            Our Values
                        </h2>
                        <p style={{ fontSize: '18px', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
                            The principles that guide everything we do at RiskAS
                        </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '32px' }}>
                        {values.map((value, index) => (
                            <div key={index} style={{
                                background: 'white',
                                padding: '32px',
                                borderRadius: '16px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                textAlign: 'center'
                            }}>
                                <div style={{ marginBottom: '24px' }}>
                                    {value.icon}
                                </div>
                                <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                                    {value.title}
                                </h3>
                                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            <section style={{ padding: '80px 0', background: 'white' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                            Our Journey
                        </h2>
                        <p style={{ fontSize: '18px', color: '#6b7280' }}>
                            Key milestones in the RiskAS story
                        </p>
                    </div>
                    <div style={{ position: 'relative' }}>
                        {/* Timeline line */}
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            background: '#e5e7eb',
                            transform: 'translateX(-50%)'
                        }}></div>

                        {milestones.map((milestone, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '40px',
                                position: 'relative'
                            }}>
                                <div style={{
                                    flex: 1,
                                    textAlign: index % 2 === 0 ? 'right' : 'left',
                                    paddingRight: index % 2 === 0 ? '40px' : '0',
                                    paddingLeft: index % 2 === 1 ? '40px' : '0'
                                }}>
                                    <div style={{
                                        background: 'white',
                                        padding: '24px',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                        border: '1px solid #f3f4f6'
                                    }}>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: '#2563eb',
                                            marginBottom: '8px'
                                        }}>
                                            {milestone.year}
                                        </div>
                                        <div style={{ color: '#374151' }}>
                                            {milestone.event}
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline dot */}
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    width: '16px',
                                    height: '16px',
                                    background: '#2563eb',
                                    borderRadius: '50%',
                                    transform: 'translateX(-50%)',
                                    border: '4px solid white',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section style={{
                padding: '80px 0',
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                color: 'white'
            }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '24px' }}>
                        Ready to Transform Your Risk Management?
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '32px', color: 'rgba(255, 255, 255, 0.9)' }}>
                        Join hundreds of companies that trust RiskAS to protect their future.
                    </p>
                    <button
                        onClick={() => window.location.href = '/register'}
                        style={{
                            background: 'white',
                            color: '#2563eb',
                            padding: '16px 32px',
                            borderRadius: '8px',
                            fontSize: '18px',
                            fontWeight: '600',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        Get Started Today
                    </button>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;